import type { ValueFormatterParams } from 'ag-grid-community';
import { AgGridInputLiveEdit } from '../../AgGrid/AgGridInputLiveEdit';
import { AgGridText, type AgGridTextParams } from '../../AgGrid/AgGridText';
import { baseColumn } from './baseColumn';
import { formatTextCellValue, type TextColumnParams } from './text';
import type { ColDefFactory, Column } from './types';
import { getFormattedCellValueColumnComparator } from './utils';

export type TextLiveEditColumnParams = TextColumnParams & {
  /**
   * Passed as the `source` argument to `node.setDataValue` on each keystroke so `readOnlyEdit` +
   * `onCellEditRequest` handlers can distinguish this pipeline from other edits.
   */
  source: string;
  /**
   * `source` argument when the user presses Escape (defaults to `source` with a trailing `Input`
   * removed, then `EscapeRollback` — e.g. `suffixInput` → `suffixEscapeRollback`).
   */
  escapeRollbackSource?: string;
  /** `aria-label` on the input; defaults to `colDef.headerName` when the editor mounts. */
  ariaLabel?: string;
};

function defaultEscapeRollbackSource(source: string): string {
  return `${source.replace(/Input$/, '')}EscapeRollback`;
}

/**
 * Text column with live edit: `setDataValue` on each change so `onCellEditRequest` runs while
 * typing (with `readOnlyEdit`). Escape rolls back the row + closes the editor; Enter stops editing.
 */
export const textLiveEdit: ColDefFactory<Column<TextLiveEditColumnParams>> = column => {
  const columnParams = column.params;
  if (columnParams == null || columnParams.source === '') {
    throw new Error('textLiveEdit column requires params with a non-empty source string');
  }
  const { source } = columnParams;
  const escapeRollbackSource = columnParams.escapeRollbackSource ?? defaultEscapeRollbackSource(columnParams.source);

  return {
    ...baseColumn(column),
    ...(columnParams.getLabel != null ? { comparator: getFormattedCellValueColumnComparator(column) } : {}),
    cellEditor: AgGridInputLiveEdit,
    cellEditorParams: {
      source,
      escapeRollbackSource,
      ariaLabel: columnParams.ariaLabel,
    },
    valueFormatter: (params: ValueFormatterParams): string => formatTextCellValue(params, columnParams),
    cellRenderer: AgGridText,
    cellRendererParams: {
      columnParams,
      showPlaceholderWhenEmpty: true,
    } satisfies AgGridTextParams,
  };
};
